/** `x-widget: "questions"` — edits an object mapping arbitrary question keys to
 *  a tag-category code, e.g. `{ "What is your dietary requirement?": "diet" }`.
 *
 * Rendered inline as a compact vertical list of rows:
 *   [key TextField] → [TagCategoryAutocomplete] [delete]
 * plus an "Add" button. Editing a key renames it while preserving row order.
 *
 * Object keys have no intrinsic order, so we keep an ordered `[key, value]`
 * entries array as the editing source of truth (seeded from `value`), and emit
 * the derived object on every change. We only re-seed from props when the
 * incoming object differs from what we last produced (external reset).
 */
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useEffect, useRef, useState } from 'react';

import { TagCategoryAutocomplete } from './TagCategoryAutocomplete';

interface Props {
  toId: number;
  value: unknown;
  onChange: (next: Record<string, string>) => void;
  readOnly?: boolean;
}

type Entry = { key: string; value: string };

function asRecord(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = typeof v === 'string' ? v : v == null ? '' : String(v);
  }
  return out;
}

function toEntries(rec: Record<string, string>): Entry[] {
  return Object.entries(rec).map(([key, value]) => ({ key, value }));
}

/** Collapse entries to an object: last non-empty key wins, empty keys dropped. */
function toObject(entries: Entry[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { key, value } of entries) {
    const k = key.trim();
    if (k) out[k] = value;
  }
  return out;
}

export function QuestionsWidget({ toId, value, onChange, readOnly = false }: Props) {
  const [entries, setEntries] = useState<Entry[]>(() => toEntries(asRecord(value)));
  // Track the object we last emitted so an external value change re-seeds rows
  // but our own edits don't clobber in-progress (e.g. empty-key) rows.
  const lastEmitted = useRef<Record<string, string>>(toObject(entries));

  useEffect(() => {
    const incoming = asRecord(value);
    if (JSON.stringify(incoming) !== JSON.stringify(lastEmitted.current)) {
      setEntries(toEntries(incoming));
      lastEmitted.current = incoming;
    }
  }, [value]);

  function commit(next: Entry[]) {
    setEntries(next);
    const obj = toObject(next);
    lastEmitted.current = obj;
    onChange(obj);
  }

  function updateKey(index: number, key: string) {
    commit(entries.map((e, i) => (i === index ? { ...e, key } : e)));
  }
  function updateValue(index: number, val: string | null) {
    commit(entries.map((e, i) => (i === index ? { ...e, value: val ?? '' } : e)));
  }
  function remove(index: number) {
    commit(entries.filter((_, i) => i !== index));
  }
  function add() {
    commit([...entries, { key: '', value: '' }]);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
      {entries.length === 0 && (
        <Typography variant="caption" color="text.disabled">
          No questions.
        </Typography>
      )}
      {entries.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Question key"
            value={entry.key}
            disabled={readOnly}
            onChange={(e) => updateKey(index, e.target.value)}
            sx={{ flex: 1, minWidth: 120 }}
          />
          <Typography variant="body2" color="text.disabled" sx={{ flexShrink: 0 }}>
            →
          </Typography>
          <Box sx={{ flex: 1, minWidth: 140 }}>
            <TagCategoryAutocomplete
              toId={toId}
              value={entry.value || null}
              onChange={(code) => updateValue(index, code)}
              readOnly={readOnly}
              placeholder="Tag category"
            />
          </Box>
          {!readOnly && (
            <Tooltip title="Remove question" placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => remove(index)}
                sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
      {!readOnly && (
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={add}
            sx={{ textTransform: 'none', minWidth: 0 }}
          >
            Add question
          </Button>
        </Box>
      )}
    </Box>
  );
}
